import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { GetLatestImpactRatingsForRatedImpactsByRatedItemIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetLatestImpactRatingsForRatedImpactsByRatedItemIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC impacts data to match the GraphQL query structure
 */
const mapTrpcImpactsToGraphQL = (
  trpcData: GetLatestImpactRatingsForRatedImpactsByRatedItemIdResponseRow[]
): GetLatestImpactRatingsForRatedImpactsByRatedItemIdQuery => {
  return { impact: trpcData };
};

export const useGetLatestImpactRatingsForRatedImpactsByRatedItemId = (
  ratedItemId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.impact.latestImpactRatingsForRatedImpactsByRatedItemId.queryOptions(
      { ratedItemId }
    ),
    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcImpactsToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
